import React, { useState, useEffect, useCallback } from 'react';
import './SellerSmsPage.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';

const headers = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

async function apiPost(path, body) {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST', headers, body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const statusMeta = {
  enabled:     { label: 'Enabled',     color: '#039855', bg: '#ecfdf5' },
  disabled:    { label: 'Disabled',    color: '#ef4444', bg: '#fef2f2' },
  pending:     { label: 'Pending',     color: '#f59e0b', bg: '#fffbeb' },
  unconfigured:{ label: 'Not Set Up',  color: '#94a3b8', bg: '#f8fafc' },
};

function sellerStatus(cfg) {
  if (!cfg.provider) return 'unconfigured';
  if (cfg.enabled)   return 'enabled';
  return 'pending';
}

const PROVIDER_NAMES = {
  nextsms:  'NextSMS',
  beem:     'Beem Africa',
  mshastra: 'Mshastra',
  fasthub:  'FastHub',
};

const SellerSmsPage = () => {
  const [sellers, setSellers]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [toggling, setToggling]   = useState({});
  const [toast, setToast]         = useState(null);
  const [search, setSearch]       = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${BACKEND_URL}/api/sms/admin/sellers`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setSellers(data.sellers || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (seller, enable) => {
    const id = seller.seller_id;
    setToggling((t) => ({ ...t, [id]: true }));
    try {
      const path = enable ? '/api/sms/admin/enable-seller' : '/api/sms/admin/disable-seller';
      await apiPost(path, { seller_id: id });
      setSellers((prev) =>
        prev.map((s) => s.seller_id === id ? { ...s, enabled: enable } : s)
      );
      const label = seller.store_name || seller.owner_name || id.slice(0, 12);
      showToast(`SMS ${enable ? 'enabled' : 'disabled'} for ${label}`);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setToggling((t) => ({ ...t, [id]: false }));
    }
  };

  const filtered = sellers.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.seller_id  || '').toLowerCase().includes(q) ||
           (s.store_name || '').toLowerCase().includes(q) ||
           (s.owner_name || '').toLowerCase().includes(q) ||
           (s.provider   || '').toLowerCase().includes(q);
  });

  const counts = {
    total:    sellers.length,
    enabled:  sellers.filter((s) => s.enabled).length,
    pending:  sellers.filter((s) => s.provider && !s.enabled).length,
  };

  return (
    <div className="ssms-page">
      {toast && (
        <div className={`ssms-toast ssms-toast--${toast.type}`}>{toast.msg}</div>
      )}

      <div className="ssms-header">
        <div>
          <h1 className="dashboard-title">Seller SMS</h1>
          <p className="dashboard-subtitle">
            Manage bulk SMS access for store owners.
          </p>
        </div>
        <button className="ssms-refresh" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {/* Summary strip */}
      <div className="ssms-strip">
        <div className="ssms-stat">
          <span className="ssms-stat-num">{counts.total}</span>
          <span className="ssms-stat-lbl">Configured</span>
        </div>
        <div className="ssms-strip-sep" />
        <div className="ssms-stat">
          <span className="ssms-stat-num ssms-green">{counts.enabled}</span>
          <span className="ssms-stat-lbl">Enabled</span>
        </div>
        <div className="ssms-strip-sep" />
        <div className="ssms-stat">
          <span className="ssms-stat-num ssms-amber">{counts.pending}</span>
          <span className="ssms-stat-lbl">Pending Approval</span>
        </div>
      </div>

      {/* Search */}
      <div className="ssms-toolbar">
        <input
          className="ssms-search"
          placeholder="Search by store name, seller or provider…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      {error && (
        <div className="ssms-error">
          {error} <button onClick={load}>Retry</button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="ssms-empty">
          {search ? 'No sellers match your search.' : 'No seller SMS configurations found.'}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="ssms-table-wrap">
          <table className="ssms-table">
            <thead>
              <tr>
                <th>Store</th>
                <th>Provider</th>
                <th>Sender ID</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const st  = sellerStatus(s);
                const sm  = statusMeta[st];
                const busy = toggling[s.seller_id];
                return (
                  <tr key={s.seller_id}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>
                        {s.store_name || <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Unknown Store</span>}
                      </div>
                      {s.owner_name && (
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{s.owner_name}</div>
                      )}
                      <code style={{ fontSize: 10, color: 'var(--text-tertiary)', background: 'var(--bg-subtle)', padding: '1px 5px', borderRadius: 3 }}>
                        {s.seller_id.slice(0, 12)}…
                      </code>
                    </td>
                    <td>{PROVIDER_NAMES[s.provider] || s.provider || '—'}</td>
                    <td>{s.sender_id || '—'}</td>
                    <td>
                      <span
                        className="ssms-chip"
                        style={{ color: sm.color, background: sm.bg }}
                      >
                        {sm.label}
                      </span>
                    </td>
                    <td>
                      {st === 'unconfigured' ? (
                        <span className="ssms-na">—</span>
                      ) : s.enabled ? (
                        <button
                          className="ssms-btn ssms-btn--danger"
                          onClick={() => toggle(s, false)}
                          disabled={busy}
                        >
                          {busy ? '…' : 'Disable'}
                        </button>
                      ) : (
                        <button
                          className="ssms-btn ssms-btn--success"
                          onClick={() => toggle(s, true)}
                          disabled={busy}
                        >
                          {busy ? '…' : 'Enable'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loading && (
        <div className="ssms-loading">
          <div className="mp-spinner" /> Loading…
        </div>
      )}
    </div>
  );
};

export default SellerSmsPage;
